"use client";

import Link from "next/link";
import { motion } from "motion/react";
import { ArrowRight, Users, BookOpen, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RainbowButton } from "@/components/ui/rainbow-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Footer } from "@/components/footer";
import { AuroraText } from "@/components/ui/aurora-text";
import { BentoCard, BentoGrid } from "@/components/ui/bento-grid";
import { WAToolsAnimatedBeam } from "@/components/ui/wa-tools-bento-item";
import { WATrainingModules } from "@/components/ui/wa-training-modules-bento-item";
import WATesterBentoItem from "@/components/ui/wa-tester-bento-item";
import { Globe } from "@/components/ui/globe";

const PLACEHOLDER_TOPICS = [
  { id: "1", title: "Getting Started with WA", tag: "wa", description: "Learn the fundamentals of Warehouse Advantage" },
  { id: "2", title: "Best Practices & Tips", tag: "tips", description: "Community-driven insights and recommendations" },
  { id: "3", title: "Integration Guides", tag: "integration", description: "Connect WA with your existing systems" },
];

const learnFeatures = [
  {
    name: "WA Foundation",
    description: "Warehouse Advantage Training",
    href: "/portal/learn",
    cta: "Start Learning",
    className: "col-span-3 lg:col-span-1",
    background: <WATrainingModules />,
  },
  {
    name: "WA Tools",
    description: "Git, Windows Server, and Deployment workflows",
    className: "col-span-3 lg:col-span-1",
    href: "/portal/learn",
    cta: "Explore Tools",
    background: <WAToolsAnimatedBeam />,
  },
  {
    name: "WA Operations",
    description: "Inbound, Picking, Packing, and Shipping",
    className: "col-span-3 lg:col-span-1",
    href: "/portal/learn",
    cta: "View Operations",
    background: <WATesterBentoItem />,
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function HomePage() {
  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 grid-background opacity-50" />

        {/* Light flare effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] bg-primary/15 rounded-full blur-[120px]"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ duration: 2, delay: 0.3, ease: "easeOut" }}
          className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 2.5, delay: 0.5, ease: "easeOut" }}
          className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[80px]"
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <motion.div variants={fadeInUp}>
              <AuroraText colors={["#0073FF", "#0064DE", "#0052CC", "#38bdf8"]} speed={2} className="text-lg font-medium">
                Infios Partners Portal
              </AuroraText>
            </motion.div>

            {/* Title */}
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground leading-tight glow-text">
              Your Gateway to the Infios Partner Ecosystem
            </motion.h1>

            {/* Subtitle */}
            <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore WA tools, training, and partner collaboration — built for developers and consultants working with Infios solutions.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/signup">
                <Button size="lg" className="min-w-[180px] group">
                  Create an Account
                </Button>
              </Link>
              <Link href="/login">
                <RainbowButton size="lg" className="min-w-[180px]">
                  Log in to the Portal
                </RainbowButton>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-8 pt-8">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">Growing Partner Network</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <BookOpen className="h-5 w-5 text-primary" />
                <span className="font-medium">Learning Paths</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MessageSquare className="h-5 w-5 text-primary" />
                <span className="font-medium">Community</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-muted-foreground/50 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Partners Preview Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="space-y-12">
            {/* Section Header */}
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <Badge variant="secondary" className="mb-4">
                <Users className="w-3 h-3 mr-1" />
                Network
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Our Partner Network</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">Connect with Infios partners across the globe</p>
            </motion.div>

            {/* Globe */}
            <motion.div variants={fadeInUp} className="flex justify-center">
              <Globe className="max-w-[600px]" />
            </motion.div>

            {/* View All Button */}
            <motion.div variants={fadeInUp} className="text-center">
              <Link href="/signup">
                <Button variant="outline" size="lg" className="group">
                  View All Partners
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Learn Preview Section */}
      <section className="py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="space-y-12">
            {/* Section Header */}
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <Badge variant="secondary" className="mb-4">
                <BookOpen className="w-3 h-3 mr-1" />
                Training
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Learn WA</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">Master Warehouse Advantage with comprehensive training modules</p>
            </motion.div>

            {/* Bento Grid */}
            <motion.div variants={fadeInUp}>
              <BentoGrid className="auto-rows-[18rem]">
                {learnFeatures.map((feature, idx) => (
                  <BentoCard key={idx} {...feature} />
                ))}
              </BentoGrid>
            </motion.div>

            {/* View All Button */}
            <motion.div variants={fadeInUp} className="text-center">
              <Link href="/signup">
                <Button variant="outline" size="lg" className="group">
                  Explore All Courses
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Forum Preview Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="space-y-12">
            {/* Section Header */}
            <motion.div variants={fadeInUp} className="text-center space-y-4">
              <Badge variant="secondary" className="mb-4">
                <MessageSquare className="w-3 h-3 mr-1" />
                Community
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Community Discussions</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">Connect, share knowledge, and get answers from fellow partners</p>
            </motion.div>

            {/* Forum Topics - Static placeholders, no real data exposed */}
            <motion.div variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {PLACEHOLDER_TOPICS.map((topic) => (
                <motion.div key={topic.id} variants={fadeInUp} whileHover={{ y: -4, transition: { duration: 0.2 } }}>
                  <Link href="/signup">
                    <Card className="h-full cursor-pointer transition-shadow hover:shadow-lg hover:shadow-primary/5 border-border/50">
                      <CardHeader>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {topic.tag}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg line-clamp-2">{topic.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{topic.description}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Join the discussion</span>
                          <span>→</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            {/* View All Button */}
            <motion.div variants={fadeInUp} className="text-center">
              <Link href="/signup">
                <Button variant="outline" size="lg" className="group">
                  Join the Conversation
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer Banner / Signup CTA */}
      <section className="py-16 bg-card border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="max-w-3xl mx-auto text-center space-y-6">
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold text-foreground">
              Ready to join the Partner Portal?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground">
              Create your account and start exploring resources, connecting with partners, and growing your expertise.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/signup">
                <Button size="lg" className="min-w-[180px] group">
                  Create Account
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link href="/login">
                <Button variant="outline" size="lg" className="min-w-[180px]">
                  Sign In
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
